import { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronsLeft, Copy, ExternalLink, RefreshCw, ShieldCheck } from "lucide-react";
import { useDash, clock } from "../../lib/dash";
import { Tile, TileState, Empty } from "./Tile";

const SOURCES = [["server", "Server"], ["audit", "Admin audit"], ["view_check", "View check"], ["spend", "Spend gate"],
  ["builds", "Build failures"], ["heavy", "Heavy worker"]];
const LEVELS = [["", "All levels"], ["WARNING", "Warning +"], ["ERROR", "Error +"]];
const LEVEL_LOOK = { ERROR: "text-rose-600", CRITICAL: "text-rose-600", WARNING: "text-amber-600", INFO: "text-sky-700" };

const line = (r) => `${r.at} ${r.level} ${r.source || ""} ${r.msg}`;

const copy = async (r) => {
  try {
    await navigator.clipboard.writeText(line(r));
    toast.success("Copied");
  } catch {
    toast.error("Could not copy");
  }
};

export const logsPath = (source, level, before) =>
  `/dash/logs?source=${source}${level ? `&level=${level}` : ""}${before ? `&before=${encodeURIComponent(before)}` : ""}`;

const LogRow = ({ r, i }) => (
  <li data-testid={`log-row-${i}`} className="group flex items-start gap-2 border-b border-border/60 py-1.5 font-mono2 text-[11.5px] last:border-0">
    <span className="w-[92px] shrink-0 text-muted-foreground">{clock(r.at)}</span>
    <span className={`w-[58px] shrink-0 font-semibold ${LEVEL_LOOK[r.level] || ""}`}>{r.level}</span>
    <span className="min-w-0 flex-1 break-words" title={r.msg}>{r.msg}</span>
    <button type="button" data-testid={`log-copy-${i}`} onClick={() => copy(r)} aria-label="Copy this line"
      className="shrink-0 rounded p-1 text-muted-foreground opacity-60 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100">
      <Copy className="h-3.5 w-3.5" />
    </button>
  </li>
);

const PagerButton = ({ testid, onClick, disabled, icon: Icon, children }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40">
    <Icon className="h-3 w-3" /> {children}
  </button>
);

// One page at a time (50 lines, newest first, last 14 days). No live stream and no polling: every read is audited.
const Pager = ({ page, data, onNewest, onOlder, onReload }) => (
  <div className="mt-2 flex items-center gap-1.5 border-t border-border/60 pt-2">
    <span data-testid="logs-page" className="font-mono2 text-[11px] text-muted-foreground">
      Page {page + 1} · {data?.page_size || 50} per page
    </span>
    <span className="ml-auto flex items-center gap-1.5">
      <PagerButton testid="logs-reload" onClick={onReload} icon={RefreshCw}>Reload</PagerButton>
      <PagerButton testid="logs-newest" onClick={onNewest} disabled={page === 0} icon={ChevronsLeft}>Newest</PagerButton>
      <PagerButton testid="logs-older" onClick={onOlder} disabled={!data?.next_before} icon={ChevronLeft}>Older</PagerButton>
    </span>
  </div>
);

const Filters = ({ source, level, onSource, onLevel }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {SOURCES.map(([key, label]) => (
      <button key={key} type="button" data-testid={`logs-source-${key}`} onClick={() => onSource(key)}
        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${source === key ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
        {label}
      </button>
    ))}
    <select data-testid="logs-level" value={level} onChange={(e) => onLevel(e.target.value)}
      className="ml-auto h-7 rounded-lg border border-input bg-background px-2 text-[11px]">
      {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export const LogsTile = ({ index }) => {
  const [source, setSource] = useState("server");
  const [level, setLevel] = useState("");
  const [cursors, setCursors] = useState([]);
  const before = cursors[cursors.length - 1];
  const state = useDash(logsPath(source, level, before), { poll: false });
  const pick = (fn) => (v) => { fn(v); setCursors([]); };
  return (
    <Tile id="logs" index={index} kicker="Tile 8 · newest first, 14 days, redacted" title="Logs"
      action={<span className="flex items-center gap-1 text-[11px] text-muted-foreground"><ExternalLink className="h-3 w-3" /> Deployments: Emergent panel</span>}>
      <Filters source={source} level={level} onSource={pick(setSource)} onLevel={pick(setLevel)} />
      <p data-testid="logs-redaction-note" className="mt-1.5 flex items-center gap-1 text-[10.5px] text-muted-foreground">
        <ShieldCheck className="h-3 w-3" /> Passwords, tokens, keys, 2-step secrets and recovery codes are hidden; emails are masked. Each read is in the admin audit.
      </p>
      <div className="mt-2 flex min-h-0 flex-1 flex-col">
        <TileState state={state}>
          {state.data?.rows?.length ? (
            <ul data-testid="logs-list" className="min-h-0 flex-1 overflow-y-auto pr-1">
              {state.data.rows.map((r, i) => <LogRow key={`${r.at}-${i}`} r={r} i={i} />)}
            </ul>
          ) : <Empty testid="logs-empty">Nothing logged here in the last 14 days.</Empty>}
        </TileState>
      </div>
      <Pager page={cursors.length} data={state.data} onReload={state.reload} onNewest={() => setCursors([])}
        onOlder={() => setCursors((c) => [...c, state.data.next_before])} />
    </Tile>
  );
};
